import { useEffect, useMemo } from 'react';
import { AnimationClip, Camera, OrthographicCamera, PerspectiveCamera } from 'three';

import { EventManager, useFrame, useThree } from '@react-three/fiber';
import { AnimatedCameraControlsImpl } from '@spazious/dollhouse-three';

export interface AnimatedCameraControlsHookOptions {
  makeDefault?: boolean;
  camera?: Camera;
  clip?: AnimationClip;
  domElement?: HTMLElement;
  onAbort?: (event: Event, reason: string) => void;
  onEnd?: () => void;
  autoPlay?: boolean;
}
/**
 * This hook allows animation keyframes to be set and played back.
 */
export const useAnimatedCameraControls = (opts?: AnimatedCameraControlsHookOptions) => {
  const { camera, clip, domElement, makeDefault, autoPlay, onAbort, onEnd } = { ...opts };
  const get = useThree(({ get }) => get);
  const set = useThree(({ set }) => set);
  const invalidate = useThree(({ invalidate }) => invalidate);
  const events = useThree(({ events }) => events) as EventManager<HTMLElement>;
  const gl = useThree(({ gl }) => gl);
  const defaultCamera = useThree((state) => state.camera);
  const explCamera = (camera || defaultCamera) as OrthographicCamera | PerspectiveCamera;
  const explDomElement = (domElement || events.connected || gl.domElement) as HTMLElement;

  const controls = useMemo(
    () => new AnimatedCameraControlsImpl(explCamera, domElement),
    [domElement, explCamera]
  );

  useEffect(() => {
    if (clip) {
      controls.setClip(clip);
    }
  }, [controls, clip]);

  useEffect(() => {
    controls.connect(explDomElement);
    return () => {
      controls.disconnect();
    };
  }, [controls, explDomElement]);

  useEffect(() => {
    const handler = () => {
      invalidate();
    };

    const abortHandler = ({ reason, event }: { reason: string; event: Event }) => {
      onAbort?.(event, reason);
      onEnd?.();
    };

    const endHandler = () => {
      onEnd?.();
    };

    controls.addEventListener('play', handler);
    controls.addEventListener('abort', abortHandler);
    controls.addEventListener('finished', endHandler);

    return () => {
      controls.removeEventListener('play', handler);
      controls.removeEventListener('abort', abortHandler);
      controls.removeEventListener('finished', endHandler);
    };
  }, [controls, invalidate, onAbort, onEnd]);

  useFrame(({ invalidate }, delta) => {
    if (controls.update(delta)) {
      invalidate();
    }
  }, -1);

  useEffect(() => {
    if (makeDefault) {
      const old = get().controls;
      set({ controls: controls as any });
      return () => set({ controls: old });
    }
  }, [makeDefault, controls, get, set]);

  useEffect(() => {
    if (autoPlay) {
      controls.play(true);
    } else {
      controls.stop();
    }
  }, [autoPlay, controls]);

  return controls;
};
